/**
 * Enhanced IndexedDB Service for Robust Offline Support
 * Version: 2.0.0
 * Date: February 11, 2026
 * 
 * Improvements over v1:
 * - Better schema organization
 * - Stock and customer caching
 * - Conflict resolution support
 * - Enhanced sync queue with priorities
 * - Transaction status tracking
 * - Batch operations support
 */

const DB_NAME = 'GSPevoPOS';
const DB_VERSION = 2; // Incremented for new schema

// Object store names
const STORES = {
  // Transaction queues
  PENDING_SALES: 'pending_sales',
  PENDING_MUTATIONS: 'pending_mutations',
  PENDING_RETURNS: 'pending_returns',
  SYNC_QUEUE: 'sync_queue',
  
  // Data caches
  ITEMS_CACHE: 'items_cache',
  STOCKS_CACHE: 'stocks_cache',
  CUSTOMERS_CACHE: 'customers_cache',
  SUPPLIERS_CACHE: 'suppliers_cache',
  
  // Metadata
  SYNC_METADATA: 'sync_metadata',
  OFFLINE_CART: 'offline_cart'
};

// TypeScript Interfaces
interface PendingSale {
  id: string;
  local_id: string; // For offline invoice tracking
  branch_id: number;
  user_id: number;
  sale_data: any;
  status: 'pending' | 'syncing' | 'synced' | 'failed';
  priority: 'high' | 'normal' | 'low';
  retry_count: number;
  max_retries: number;
  created_at: number;
  synced_at?: number;
  last_error?: string;
  conflict_data?: any; // For conflict resolution
}

interface SyncQueueItem {
  id: string;
  type: 'sale' | 'mutation' | 'return' | 'stock_adjust' | 'other';
  endpoint: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  data: any;
  priority: 'high' | 'normal' | 'low';
  retry_count: number;
  max_retries: number;
  created_at: number;
  next_retry_at?: number;
  dependencies?: string[]; // IDs of items that must sync first
}

interface CachedItem {
  id: number;
  data: any;
  cached_at: number;
  expires_at: number;
  version: number; // For conflict detection
}

interface SyncMetadata {
  key: string;
  value: any;
  updated_at: number;
}

class EnhancedIndexedDBService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize database with enhanced schema
   */
  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB open error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✓ IndexedDB initialized successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const oldVersion = event.oldVersion;

        console.log(`Upgrading IndexedDB from version ${oldVersion} to ${DB_VERSION}`);

        // Create or upgrade stores
        this.createPendingSalesStore(db);
        this.createPendingMutationsStore(db);
        this.createPendingReturnsStore(db);
        this.createSyncQueueStore(db);
        this.createItemsCacheStore(db);
        this.createStocksCacheStore(db);
        this.createCustomersCacheStore(db);
        this.createSuppliersCacheStore(db);
        this.createSyncMetadataStore(db);
        this.createOfflineCartStore(db);

        console.log('✓ All object stores created/upgraded');
      };
    });

    return this.initPromise;
  }

  // Store creation methods
  private createPendingSalesStore(db: IDBDatabase) {
    if (!db.objectStoreNames.contains(STORES.PENDING_SALES)) {
      const store = db.createObjectStore(STORES.PENDING_SALES, { keyPath: 'id' });
      store.createIndex('status', 'status', { unique: false });
      store.createIndex('created_at', 'created_at', { unique: false });
      store.createIndex('priority', 'priority', { unique: false });
      store.createIndex('branch_id', 'branch_id', { unique: false });
      store.createIndex('user_id', 'user_id', { unique: false });
      store.createIndex('status_priority', ['status', 'priority'], { unique: false });
    }
  }

  private createPendingMutationsStore(db: IDBDatabase) {
    if (!db.objectStoreNames.contains(STORES.PENDING_MUTATIONS)) {
      const store = db.createObjectStore(STORES.PENDING_MUTATIONS, { keyPath: 'id' });
      store.createIndex('status', 'status', { unique: false });
      store.createIndex('created_at', 'created_at', { unique: false });
    }
  }

  private createPendingReturnsStore(db: IDBDatabase) {
    if (!db.objectStoreNames.contains(STORES.PENDING_RETURNS)) {
      const store = db.createObjectStore(STORES.PENDING_RETURNS, { keyPath: 'id' });
      store.createIndex('status', 'status', { unique: false });
      store.createIndex('created_at', 'created_at', { unique: false });
    }
  }

  private createSyncQueueStore(db: IDBDatabase) {
    if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
      const store = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id' });
      store.createIndex('type', 'type', { unique: false });
      store.createIndex('priority', 'priority', { unique: false });
      store.createIndex('created_at', 'created_at', { unique: false });
      store.createIndex('next_retry_at', 'next_retry_at', { unique: false });
      store.createIndex('type_priority', ['type', 'priority'], { unique: false });
    }
  }

  private createItemsCacheStore(db: IDBDatabase) {
    if (!db.objectStoreNames.contains(STORES.ITEMS_CACHE)) {
      const store = db.createObjectStore(STORES.ITEMS_CACHE, { keyPath: 'id' });
      store.createIndex('cached_at', 'cached_at', { unique: false });
      store.createIndex('expires_at', 'expires_at', { unique: false });
      store.createIndex('sku', 'data.sku', { unique: false });
      store.createIndex('barcode', 'data.barcode', { unique: false });
    }
  }

  private createStocksCacheStore(db: IDBDatabase) {
    if (!db.objectStoreNames.contains(STORES.STOCKS_CACHE)) {
      const store = db.createObjectStore(STORES.STOCKS_CACHE, { keyPath: 'id' });
      store.createIndex('branch_item', ['data.branch_id', 'data.item_id'], { unique: false });
      store.createIndex('branch_id', 'data.branch_id', { unique: false });
      store.createIndex('item_id', 'data.item_id', { unique: false });
      store.createIndex('cached_at', 'cached_at', { unique: false });
    }
  }

  private createCustomersCacheStore(db: IDBDatabase) {
    if (!db.objectStoreNames.contains(STORES.CUSTOMERS_CACHE)) {
      const store = db.createObjectStore(STORES.CUSTOMERS_CACHE, { keyPath: 'id' });
      store.createIndex('phone', 'data.phone', { unique: false });
      store.createIndex('cached_at', 'cached_at', { unique: false });
    }
  }

  private createSuppliersCacheStore(db: IDBDatabase) {
    if (!db.objectStoreNames.contains(STORES.SUPPLIERS_CACHE)) {
      const store = db.createObjectStore(STORES.SUPPLIERS_CACHE, { keyPath: 'id' });
      store.createIndex('cached_at', 'cached_at', { unique: false });
    }
  }

  private createSyncMetadataStore(db: IDBDatabase) {
    if (!db.objectStoreNames.contains(STORES.SYNC_METADATA)) {
      db.createObjectStore(STORES.SYNC_METADATA, { keyPath: 'key' });
    }
  }

  private createOfflineCartStore(db: IDBDatabase) {
    if (!db.objectStoreNames.contains(STORES.OFFLINE_CART)) {
      db.createObjectStore(STORES.OFFLINE_CART, { keyPath: 'id' });
    }
  }

  private async ensureDB(): Promise<IDBDatabase> {
    // If initialization is in progress, wait for it
    if (this.initPromise && !this.db) {
      await this.initPromise;
    }
    
    // If still not initialized, throw error
    if (!this.db) {
      throw new Error('Database not initialized. Call init() first.');
    }
    return this.db;
  }

  /**
   * Check if database is initialized (for external checks)
   */
  isInitialized(): boolean {
    return this.db !== null;
  }

  /**
   * Wait for database to be initialized
   */
  async waitForInit(): Promise<void> {
    // If already initialized, return immediately
    if (this.db) {
      return;
    }
    
    // If initialization is in progress, wait for it
    if (this.initPromise) {
      await this.initPromise;
      return;
    }
    
    // If init hasn't been called yet, wait with retry logic
    const maxRetries = 50; // 50 * 100ms = 5 seconds max
    for (let i = 0; i < maxRetries; i++) {
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (this.db) {
        return;
      }
      
      if (this.initPromise) {
        await this.initPromise;
        return;
      }
    }
    
    // Still not initialized after retries - this is OK, just log it
    console.warn('Database initialization timeout - service may not be ready yet');
  }

  // ============================================================
  // PENDING SALES OPERATIONS
  // ============================================================

  /**
   * Save a sale for offline processing
   */
  async savePendingSale(
    saleData: any,
    priority: 'high' | 'normal' | 'low' = 'high'
  ): Promise<string> {
    const db = await this.ensureDB();
    const timestamp = Date.now();
    const id = `sale_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
    const localId = `OFF-${timestamp}`;

    const pendingSale: PendingSale = {
      id,
      local_id: localId,
      branch_id: saleData.branch_id,
      user_id: saleData.user_id,
      sale_data: saleData,
      status: 'pending',
      priority,
      retry_count: 0,
      max_retries: 5,
      created_at: timestamp
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SALES], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_SALES);
      const request = store.add(pendingSale);

      request.onsuccess = () => {
        console.log(`✓ Sale saved offline: ${id} (${localId})`);
        resolve(localId); // Return local ID for invoice
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all pending sales, optionally filtered by status
   */
  async getPendingSales(status?: string): Promise<PendingSale[]> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SALES], 'readonly');
      const store = transaction.objectStore(STORES.PENDING_SALES);

      let request: IDBRequest;
      if (status) {
        const index = store.index('status');
        request = index.getAll(status);
      } else {
        request = store.getAll();
      }

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get pending sales sorted by priority
   */
  async getPendingSalesByPriority(): Promise<PendingSale[]> {
    const sales = await this.getPendingSales('pending');
    
    // Sort: high > normal > low, then by created_at
    return sales.sort((a, b) => {
      const priorityOrder = { high: 0, normal: 1, low: 2 };
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
      if (priorityDiff !== 0) return priorityDiff;
      return a.created_at - b.created_at;
    });
  }

  /**
   * Update pending sale status
   */
  async updatePendingSaleStatus(
    id: string,
    status: PendingSale['status'],
    error?: string
  ): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SALES], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_SALES);
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const sale = getRequest.result;
        if (!sale) {
          reject(new Error(`Sale ${id} not found`));
          return;
        }

        sale.status = status;
        if (status === 'failed') {
          sale.retry_count += 1;
          sale.last_error = error;
        } else if (status === 'synced') {
          sale.synced_at = Date.now();
        }

        const updateRequest = store.put(sale);
        updateRequest.onsuccess = () => resolve();
        updateRequest.onerror = () => reject(updateRequest.error);
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  /**
   * Delete a pending sale (after successful sync)
   */
  async deletePendingSale(id: string): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.PENDING_SALES], 'readwrite');
      const store = transaction.objectStore(STORES.PENDING_SALES);
      const request = store.delete(id);

      request.onsuccess = () => {
        console.log(`✓ Deleted synced sale: ${id}`);
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ============================================================
  // SYNC QUEUE OPERATIONS (Generic)
  // ============================================================

  /**
   * Add item to sync queue
   */
  async addToSyncQueue(item: Omit<SyncQueueItem, 'id' | 'retry_count' | 'created_at'>): Promise<string> {
    const db = await this.ensureDB();
    const timestamp = Date.now();
    const id = `queue_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;

    const queueItem: SyncQueueItem = {
      ...item,
      id,
      retry_count: 0,
      created_at: timestamp
    };

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_QUEUE], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const request = store.add(queueItem);

      request.onsuccess = () => {
        console.log(`✓ Added to sync queue: ${id} (${item.type})`);
        resolve(id);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get sync queue items sorted by priority
   */
  async getSyncQueue(): Promise<SyncQueueItem[]> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_QUEUE], 'readonly');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const request = store.getAll();

      request.onsuccess = () => {
        const items = request.result || [];
        
        // Sort by priority and created_at
        items.sort((a, b) => {
          const priorityOrder = { high: 0, normal: 1, low: 2 };
          const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
          if (priorityDiff !== 0) return priorityDiff;
          return a.created_at - b.created_at;
        });

        resolve(items);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Remove item from sync queue
   */
  async removeFromSyncQueue(id: string): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_QUEUE], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ============================================================
  // CACHE OPERATIONS
  // ============================================================

  /**
   * Cache items with expiration
   */
  async cacheItems(items: any[], ttlMinutes: number = 60): Promise<void> {
    const db = await this.ensureDB();
    const timestamp = Date.now();
    const expiresAt = timestamp + (ttlMinutes * 60 * 1000);

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.ITEMS_CACHE], 'readwrite');
      const store = transaction.objectStore(STORES.ITEMS_CACHE);

      // Clear old cache
      store.clear();

      // Add new items
      items.forEach(item => {
        const cachedItem: CachedItem = {
          id: item.id,
          data: item,
          cached_at: timestamp,
          expires_at: expiresAt,
          version: 1
        };
        store.add(cachedItem);
      });

      transaction.oncomplete = () => {
        console.log(`✓ Cached ${items.length} items (TTL: ${ttlMinutes}min)`);
        this.updateSyncMetadata('last_items_cache', timestamp);
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Get cached items (only valid ones)
   */
  async getCachedItems(): Promise<any[]> {
    const db = await this.ensureDB();
    const now = Date.now();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.ITEMS_CACHE], 'readonly');
      const store = transaction.objectStore(STORES.ITEMS_CACHE);
      const request = store.getAll();

      request.onsuccess = () => {
        const cached = request.result || [];
        
        // Filter out expired items
        const valid = cached.filter(item => item.expires_at > now);
        
        console.log(`✓ Retrieved ${valid.length} cached items (${cached.length - valid.length} expired)`);
        
        resolve(valid.map(item => item.data));
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Search cached items by SKU or barcode
   */
  async searchCachedItems(query: string): Promise<any[]> {
    const items = await this.getCachedItems();
    const lowerQuery = query.toLowerCase();

    return items.filter(item => 
      item.sku?.toLowerCase().includes(lowerQuery) ||
      item.barcode?.toLowerCase().includes(lowerQuery) ||
      item.name?.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * Cache stocks with expiration
   */
  async cacheStocks(stocks: any[], ttlMinutes: number = 30): Promise<void> {
    const db = await this.ensureDB();
    const timestamp = Date.now();
    const expiresAt = timestamp + (ttlMinutes * 60 * 1000);

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.STOCKS_CACHE], 'readwrite');
      const store = transaction.objectStore(STORES.STOCKS_CACHE);

      store.clear();

      stocks.forEach(stock => {
        const cachedStock: CachedItem = {
          id: stock.id,
          data: stock,
          cached_at: timestamp,
          expires_at: expiresAt,
          version: 1
        };
        store.add(cachedStock);
      });

      transaction.oncomplete = () => {
        console.log(`✓ Cached ${stocks.length} stocks (TTL: ${ttlMinutes}min)`);
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Get cached stocks for a branch
   */
  async getCachedStocksByBranch(branchId: number): Promise<any[]> {
    const db = await this.ensureDB();
    const now = Date.now();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.STOCKS_CACHE], 'readonly');
      const store = transaction.objectStore(STORES.STOCKS_CACHE);
      const index = store.index('branch_id');
      const request = index.getAll(branchId);

      request.onsuccess = () => {
        const cached = request.result || [];
        const valid = cached.filter(item => item.expires_at > now);
        resolve(valid.map(item => item.data));
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Cache customers
   */
  async cacheCustomers(customers: any[], ttlMinutes: number = 120): Promise<void> {
    const db = await this.ensureDB();
    const timestamp = Date.now();
    const expiresAt = timestamp + (ttlMinutes * 60 * 1000);

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.CUSTOMERS_CACHE], 'readwrite');
      const store = transaction.objectStore(STORES.CUSTOMERS_CACHE);

      store.clear();

      customers.forEach(customer => {
        const cachedCustomer: CachedItem = {
          id: customer.id,
          data: customer,
          cached_at: timestamp,
          expires_at: expiresAt,
          version: 1
        };
        store.add(cachedCustomer);
      });

      transaction.oncomplete = () => {
        console.log(`✓ Cached ${customers.length} customers`);
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Get cached customers
   */
  async getCachedCustomers(): Promise<any[]> {
    const db = await this.ensureDB();
    const now = Date.now();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.CUSTOMERS_CACHE], 'readonly');
      const store = transaction.objectStore(STORES.CUSTOMERS_CACHE);
      const request = store.getAll();

      request.onsuccess = () => {
        const cached = request.result || [];
        const valid = cached.filter(item => item.expires_at > now);
        resolve(valid.map(item => item.data));
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ============================================================
  // METADATA OPERATIONS
  // ============================================================

  /**
   * Update sync metadata
   */
  async updateSyncMetadata(key: string, value: any): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_METADATA], 'readwrite');
      const store = transaction.objectStore(STORES.SYNC_METADATA);

      const metadata: SyncMetadata = {
        key,
        value,
        updated_at: Date.now()
      };

      const request = store.put(metadata);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get sync metadata
   */
  async getSyncMetadata(key: string): Promise<any> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.SYNC_METADATA], 'readonly');
      const store = transaction.objectStore(STORES.SYNC_METADATA);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result?.value);
      request.onerror = () => reject(request.error);
    });
  }

  // ============================================================
  // OFFLINE CART OPERATIONS
  // ============================================================

  /**
   * Save cart for offline persistence
   */
  async saveOfflineCart(cartData: any): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.OFFLINE_CART], 'readwrite');
      const store = transaction.objectStore(STORES.OFFLINE_CART);
      
      const cart = {
        id: 'current',
        data: cartData,
        timestamp: Date.now()
      };

      const request = store.put(cart);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get offline cart
   */
  async getOfflineCart(): Promise<any | null> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.OFFLINE_CART], 'readonly');
      const store = transaction.objectStore(STORES.OFFLINE_CART);
      const request = store.get('current');

      request.onsuccess = () => resolve(request.result?.data || null);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear offline cart
   */
  async clearOfflineCart(): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.OFFLINE_CART], 'readwrite');
      const store = transaction.objectStore(STORES.OFFLINE_CART);
      const request = store.delete('current');

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ============================================================
  // STATISTICS & UTILITIES
  // ============================================================

  /**
   * Get comprehensive stats
   */
  async getStats(): Promise<{
    pendingSales: number;
    pendingMutations: number;
    pendingReturns: number;
    syncQueue: number;
    cachedItems: number;
    cachedStocks: number;
    cachedCustomers: number;
    lastItemsCache?: number;
    lastSync?: number;
    totalPendingTransactions: number;
  }> {
    const [
      pendingSales,
      syncQueue,
      cachedItems,
      cachedStocks,
      cachedCustomers,
      lastItemsCache,
      lastSync
    ] = await Promise.all([
      this.getPendingSales('pending'),
      this.getSyncQueue(),
      this.getCachedItems(),
      this.getCachedStocks(),
      this.getCachedCustomers(),
      this.getSyncMetadata('last_items_cache'),
      this.getSyncMetadata('last_sync')
    ]);

    return {
      pendingSales: pendingSales.length,
      pendingMutations: 0, // TODO: Implement
      pendingReturns: 0, // TODO: Implement
      syncQueue: syncQueue.length,
      cachedItems: cachedItems.length,
      cachedStocks: cachedStocks.length,
      cachedCustomers: cachedCustomers.length,
      lastItemsCache,
      lastSync,
      totalPendingTransactions: pendingSales.length + syncQueue.length
    };
  }

  /**
   * Get all cached stocks (valid only)
   */
  private async getCachedStocks(): Promise<any[]> {
    const db = await this.ensureDB();
    const now = Date.now();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORES.STOCKS_CACHE], 'readonly');
      const store = transaction.objectStore(STORES.STOCKS_CACHE);
      const request = store.getAll();

      request.onsuccess = () => {
        const cached = request.result || [];
        const valid = cached.filter(item => item.expires_at > now);
        resolve(valid.map(item => item.data));
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear all data (for testing/reset)
   */
  async clearAllData(): Promise<void> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(Object.values(STORES), 'readwrite');

      Object.values(STORES).forEach(storeName => {
        transaction.objectStore(storeName).clear();
      });

      transaction.oncomplete = () => {
        console.log('✓ All offline data cleared');
        resolve();
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Export all data (for debugging)
   */
  async exportAllData(): Promise<any> {
    const stats = await this.getStats();
    const pendingSales = await this.getPendingSales();
    const syncQueue = await this.getSyncQueue();
    const cachedItems = await this.getCachedItems();

    return {
      stats,
      pendingSales,
      syncQueue,
      cachedItems: cachedItems.slice(0, 10) // First 10 only
    };
  }
}

// Export singleton instance
export const enhancedIndexedDBService = new EnhancedIndexedDBService();
export default enhancedIndexedDBService;

// Export types
export type {
  PendingSale,
  SyncQueueItem,
  CachedItem,
  SyncMetadata
};
